import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { instantGamesListQO } from "@/lib/instant-games.functions";
import { InstantGameCard } from "@/components/InstantGameCard";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
  stateSlug: string;
  activeCategory?: string | null;
};

const HIDDEN_CATEGORIES = new Set(["derby cash", "fast play"]);
const isHidden = (c?: string | null) =>
  typeof c === "string" && HIDDEN_CATEGORIES.has(c.trim().toLowerCase());

export function InstantGamesSection({ stateSlug, activeCategory = "all" }: Props) {
  const { data: games = [] } = useQuery(instantGamesListQO(stateSlug));
  const navigate = useNavigate();

  const categories = useMemo(() => {
    const seen = new Set<string>();
    games.forEach((g) => g.categories.forEach((c) => seen.add(c)));
    return Array.from(seen).filter((c) => !isHidden(c)).sort();
  }, [games]);

  // `activeCategory` can arrive as undefined/null when the URL has no `?ig=`
  // (or a validator default is bypassed) — always fall back to "all".
  const category = typeof activeCategory === "string" && activeCategory.trim() ? activeCategory : "all";
  const effectiveCategory = isHidden(category) ? "all" : category;

  const filtered = useMemo(() => {
    if (effectiveCategory === "all") return games;
    return games.filter((g) => g.categories.includes(effectiveCategory));
  }, [games, effectiveCategory]);

  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [effectiveCategory]);

  if (!games.length) return null;

  return (
    <section className="mb-10">
      <h2 className="font-display text-xl font-bold mb-4">Instant Games</h2>

      <div className="flex flex-wrap justify-center gap-2 mb-6">
        <CategoryPill
          label="All"
          active={effectiveCategory === "all"}
          onClick={() => navigate({ to: ".", search: (prev: any) => ({ ...prev, ig: undefined }), resetScroll: false })}
        />
        {categories.map((c) => (
          <CategoryPill
            key={c}
            label={c}
            active={effectiveCategory === c}
            onClick={() => navigate({ to: ".", search: (prev: any) => ({ ...prev, ig: c }), resetScroll: false })}
          />
        ))}
      </div>

      <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {filtered.slice(0, visibleCount).map((g) => (
          <InstantGameCard key={g.slug} game={g} stateSlug={stateSlug} />
        ))}
      </div>
      {filtered.length > visibleCount && (
        <div className="flex justify-center mt-6">
          <Button
            variant="outline"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          >
            Load More
          </Button>
        </div>
      )}
      {filtered.length === 0 && (
        <p className="text-center text-sm text-muted-foreground mt-6">No games in this category.</p>
      )}
    </section>
  );
}

function CategoryPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-9 px-5 inline-flex items-center rounded-full text-sm font-semibold transition-colors border",
        active
          ? "bg-accent text-accent-foreground border-accent shadow-sm hover:bg-accent/90"
          : "bg-card text-foreground border-border hover:bg-accent hover:text-accent-foreground hover:border-accent",
      )}
    >
      {label}
    </button>
  );
}
